import { Injectable } from '@angular/core';
import {
    Action,
    LegacyActionHandlerBody,
    LegacyActionName,
    ActionWrappedArrayBody,
    ActionObjectBody,
    isActionWrappedArrayBody,
    isActionObjectBody,
    LegacyAction,
    isLegacyAction,
    isActionEntityBody,
    ActionEntityBody,
} from '@ddv/models';

import { ActionRequestBody, ArrayBody, EntityPayloadBody, WrappedArrayPayloadBody, WrappedObjectPayloadBody } from '../models/action-handler';

@Injectable()
export class ActionHandlerBodyBuilderService {
    buildActionHandlerBody(
        action: Action | LegacyAction,
        selectedRows: Record<string, unknown>[],
        isTFLIncompleteFiles: boolean,
    ): ActionRequestBody | undefined {
        // legacy actions should be adapted to the new format
        if (isLegacyAction(action)) {
            const actionName = action.name;
            if (actionName === LegacyActionName.Reprocess) {
                return buildReprocessPayloadBody(action.handler.body, selectedRows, isTFLIncompleteFiles);
            }
            if (actionName === LegacyActionName.Acknowledge) {
                return buildAcknowledgePayloadBody(action.handler.body, selectedRows, isTFLIncompleteFiles);
            }
        }

        const actionBody = action.handler.body;
        if (isActionWrappedArrayBody(actionBody)) {
            return buildWrappedPayloadBody(actionBody, selectedRows);
        }

        if (isActionObjectBody(actionBody)) {
            return buildObjectPayloadBody(actionBody, selectedRows);
        }

        if (isActionEntityBody(actionBody)) {
            return buildEntityPayloadBody(actionBody, selectedRows[0]);
        }

        console.error('Unhandled action');
    }
}

function buildReprocessPayloadBody(
    config: LegacyActionHandlerBody,
    selectedRows: Record<string, unknown>[],
    isTFLIncompleteFiles: boolean,
): WrappedObjectPayloadBody | ArrayBody {
    const tradeHistoryIdsSet = new Set();
    selectedRows.forEach((row) => {
        const item: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(config.items!.properties)) {
            item[key] = row[value] || '';
        }
        tradeHistoryIdsSet.add(JSON.stringify(item));
    });

    return isTFLIncompleteFiles ?
        { fileIds: [...tradeHistoryIdsSet] } :
        [...tradeHistoryIdsSet].map((item: unknown) => JSON.parse(String(item)));
}

function buildAcknowledgePayloadBody(
    config: LegacyActionHandlerBody,
    selectedRows: Record<string, unknown>[],
    isTFLIncompleteFiles: boolean,
): WrappedObjectPayloadBody | ArrayBody {
    const tradeHistoryIdsSet = new Set();
    const columnId = config.items!.properties.id;

    selectedRows.forEach((row) => {
        if (isTFLIncompleteFiles) {
            tradeHistoryIdsSet.add(row[columnId]);
        } else {
            tradeHistoryIdsSet.add(JSON.stringify({ id: row[columnId] }));
        }
    });

    return isTFLIncompleteFiles ?
        { fileIds: [...tradeHistoryIdsSet] } :
        [...tradeHistoryIdsSet].map((item: unknown) => JSON.parse(String(item)));
}

function buildWrappedPayloadBody(config: ActionWrappedArrayBody, selectedRows: Record<string, unknown>[]): WrappedArrayPayloadBody {
    const wrapperPropertyName = config.wrapperPropertyName;

    return {
        [wrapperPropertyName]: selectedRows,
    };
}

function buildObjectPayloadBody(config: ActionObjectBody, selectedRows: Record<string, unknown>[]): WrappedObjectPayloadBody {
    const { rowValuePropertyName, rowValueFieldName, rowKeyFieldName, wrapperPropertyName } = config;
    const payloadBody: Record<string, Record<string, unknown>> = { [wrapperPropertyName]: {} };

    selectedRows.forEach((row) => {
        const rowWrapper = row[rowKeyFieldName] as string;
        payloadBody[wrapperPropertyName][rowWrapper] = { [rowValuePropertyName]: row[rowValueFieldName] };
    });

    return payloadBody;
}

function buildEntityPayloadBody(config: ActionEntityBody, selectedRow: Record<string, unknown>): EntityPayloadBody {
    const { keyPropertyName, keyFieldName, valuePropertyName, valueFieldName } = config;
    return {
        key: {
            fieldname: keyPropertyName,
            value: String(selectedRow[keyFieldName]),
        },
        value: {
            fieldname: valuePropertyName,
            value: String(selectedRow[valueFieldName]),
        },
    };
}
